import Cookies from "js-cookie";
import cookie from "cookie";
import { useRouter } from "next/router";
import { publicConfig } from "@lululemon/ecom-core";
import { log } from "@lululemon/mwa-utils";

export const EN_US_STANDARD_LOCALE = "en-US";
export const EN_CA_STANDARD_LOCALE = "en-CA";
export const FR_CA_STANDARD_LOCALE = "fr-CA";

// TODO: Regional Accuracy - Phase 4 - Remove UsrLocale cookie
export function setUsrSl(sl) {
  Cookies.set("sl", sl, {
    domain: ".lululemon.com",
    expires: 30,
    secure: true,
  });
}

export function setUsrLocale(locale, sl = "US") {
  // UsrLocale must be in 'en_US' format not 'en-US'
  const underscoreLocale = locale.replace("-", "_");
  Cookies.set("UsrLocale", underscoreLocale, {
    domain: ".lululemon.com",
    expires: 30,
    secure: true,
  });

  if (sl) {
    setUsrSl(sl);
  }
}

export function getUsrLocale() {
  return Cookies.get("UsrLocale") || "en_US";
}

export function getUsrLocaleFromCookie(req) {
  return typeof window !== "undefined"
    ? getUsrLocale()
    : req?.cookies?.UsrLocale || "en_US";
}

/**
 * Returns `true` if the user's selected locale matches the supplied locale.
 *
 * @example
 *
 * // `true`
 * const isFrCA = isLocale('fr_CA');
 *
 * @param {String} locale The locale code to check against, EG: 'en_US'
 * @returns {Boolean}
 */
export function isUsrLocale(locale) {
  if (!locale) {
    throw new Error("Must supply a `locale`.");
  }

  return getUsrLocaleStandard() === locale.replace("_", "-");
}

/**
 * Returns the user locale received via querystring params.
 *
 * @examples
 * querystring contains &locale=foo_BAR
 *  query = {locale: "foo_BAR"} => "foo_BAR"
 *
 * querystring contains &locale=bar_BAZ&locale=foo_BAR
 *  query = {locale: ["bar_BAZ", "foo_BAR"]}=> "foo_BAR"
 *
 * @param {object} query An object contaning all the query params received via
 * querystring.
 * @returns String
 */
export function getLocaleFromQueryParams(query) {
  let locale = query?.locale;
  // Use the first locale in case that more than one are passed.
  // (For example: &locale=en_US&locale=en_US).
  if (Array.isArray(locale)) {
    locale = locale[0];
  }
  return locale;
}

/**
 * Returns the user's `UsrLocale` in the
 * format with underscore.
 *
 * @example
 * 'fr_CA', 'en_US', 'en_CA'
 *
 * @returns String Standardized Locale
 */
export function getLocaleForContext(
  params = {},
  shouldSetCookie = false,
  options = {}
) {
  const { req = {}, res = {} } = params;
  const { url, query, cookies } = req;
  let locale;
  let sl;
  let underscoredLocale;

  // Regional Accuracy - Phase 4 - Remove UsrLocale cookie
  const cookieLocale = Cookies.get("UsrLocale");
  const reqCookies = req?.cookies;

  const cookieSl = Cookies.get("sl");

  const nextLocale = options?.locale || "";

  // HANDLE fr.shop.lululemon.com and fr.stage.lululemon.com, etc.
  // If cookie.locale is fr_CA then return fr-ca and fr_CA
  if (
    publicConfig.apiHost?.includes("https://fr.") ||
    url?.startsWith("/fr-ca")
  ) {
    locale = FR_CA_STANDARD_LOCALE;
    underscoredLocale = "fr_CA";
    sl = "CA";
  } else if (url?.startsWith("/en-ca")) {
    locale = EN_CA_STANDARD_LOCALE;
    underscoredLocale = "en_CA";
    sl = "CA";
  } else {
    // Regional Accuracy - Phase 4 - Remove query params for locale
    sl = query?.sl || req?.cookies?.sl || cookies?.sl || cookieSl || "US";

    // TODO: Regional Accuracy Phase 1 code complete, uncomment usage of LaunchDarkly flag
    const regionalAccuracyPhase0 = true;

    // TODO: regionalAccuracyPhase0 should be true before releasing to Shop
    if (regionalAccuracyPhase0) {
      // undefined is falsy
      locale =
        req?.cookies?.UsrLocale ||
        getLocaleFromQueryParams(query) ||
        cookies?.UsrLocale ||
        undefined;
      underscoredLocale = locale?.replace("-", "_");
    }
  }

  /**
   * When request is made to _next, we need to return the locale from the cookie, since nextLocale is returning default locale
   */
  if (req?.originalUrl?.includes("_next")) {
    return reqCookies?.UsrLocale.replace("_", "-");
  }

  if (nextLocale) {
    switch (nextLocale) {
      case "en-ca":
        locale = EN_CA_STANDARD_LOCALE;
        underscoredLocale = "en_CA";
        sl = "CA";

        break;
      case "en-us":
        locale = EN_US_STANDARD_LOCALE;
        underscoredLocale = "en_US";
        sl = "US";
        break;

      case "fr-ca":
        locale = FR_CA_STANDARD_LOCALE;
        underscoredLocale = "fr_CA";
        sl = "CA";
        break;
    }
    syncCookies(reqCookies, res, underscoredLocale, sl);
  }

  // Return the locale from 1. Url (/en-ca or ?locale=) or 2. cookie
  return (locale || cookieLocale || EN_US_STANDARD_LOCALE).replace("_", "-");
}

function syncCookies(reqCookies, res, underscoredLocale, sl) {
  if (underscoredLocale !== reqCookies?.UsrLocale) {
    if (typeof window === "undefined") {
      const usrLocaleCookieValue = `UsrLocale=${underscoredLocale}; path=/; domain=.lululemon.com; secure; SameSite=None`;
      const slCookieValue = `sl=${sl}; path=/; domain=.lululemon.com; secure; SameSite=None`;
      const pendingCookiesToSet = res.getHeader("set-cookie") || [];

      // Check to see if the response cookie header already contains the expected UsrLocale and sl cookie
      // values. If not, append them to the response cookie header.
      !pendingCookiesToSet.includes(usrLocaleCookieValue) &&
        res.append("set-cookie", usrLocaleCookieValue);
      !pendingCookiesToSet.includes(slCookieValue) &&
        res.append("set-cookie", slCookieValue);
    } else {
      setUsrLocale(underscoredLocale, sl);
    }
  }
}

/**
 * Updates the user's `UsrLocale` cookie to match context props
 *
 * @example
 * 'fr_CA', 'en_US', 'en_CA'
 *
 */
export function syncUsrLocaleCookie(locale) {
  const underscoredLocale = locale?.replace("-", "_") || "en_US";
  const cookieLocale = Cookies.get("UsrLocale");
  const cookieSl = Cookies.get("sl");
  // Fallback to 'US' if no local or cookieSL
  let sl = "US";
  if (locale === EN_CA_STANDARD_LOCALE || locale === FR_CA_STANDARD_LOCALE) {
    sl = "CA";
  } else if (cookieSl !== "CA") {
    // If cookie is not CA use the cookie value
    sl = cookieSl;
  }

  if (locale && underscoredLocale !== cookieLocale) {
    setUsrLocale(underscoredLocale, sl);
  }
}

/**
 * Returns the user's `UsrLocale` in the
 * standard format with dashes.
 *
 * @example
 * 'fr-CA', 'en-US', 'en-CA'
 *
 * @returns String Standardized Locale
 */
export function getUsrLocaleStandard() {
  // Regional Accuracy - Phase 4 - Remove UsrLocale cookie
  const locale = Cookies.get("UsrLocale") || "en_US"; // Fallback
  return locale.replace("_", "-");
}

/**
 * Returns the user's `UsrLocale` parsed into object.
 *
 * @example
 * { lang: 'en', countryCode: 'US' }
 *
 * @returns Object Parsed locale
 */
export const getParsedLocale = () => {
  const standardLocale = getUsrLocaleStandard();
  const [lang, countryCode] = standardLocale.split("-");

  return { lang: lang.toLowerCase(), countryCode: countryCode.toUpperCase() };
};

/**
 * @param region
 * @returns Boolean
 */
export const isRegion = (region) => {
  if (typeof region !== "string" || !region) {
    return false;
  }
  return getUsrLocale()?.toLowerCase().endsWith(region?.toLowerCase());
};

// Regional Accuracy - Phase 4 - Remove UsrLocale cookie
export const getRequestUsrLocale = (req) => {
  const reqCookie = cookie.parse(req.headers?.cookie || "");
  return {
    cookieUsrLocale: reqCookie.UsrLocale?.replace("_", "-"),
    reqCookie,
  };
};

export const ensureLocaleCookie = (reqCookie, usrLocale, cookieString = "") => {
  const parsedCookie = cookieString ? cookie.parse(cookieString) : reqCookie;
  parsedCookie.UsrLocale = usrLocale?.replace("-", "_");

  return Object.keys(parsedCookie)
    .map((c) => cookie.serialize(c, parsedCookie[c]))
    .join(";");
};

/**
 * Returns location including regionalized
 * formats of /en-ca/ & /fr-ca/
 *
 * @example
 * https://localdev.lululemon.com:5000/en-ca/
 *
 * @param {path} string A string of the href
 * @returns {object} url object
 * @description The hook exposes a function that will update URLs to their regionalized hostname and subpath
 */
export const useRegionalize = () => {
  const { locale: nextLocale, locales } = useRouter();
  const { i18nDomains } = useLocaleHosts();
  /**
   *
   * @param {string} path - The path to remove the regionalized subpath from
   * @returns
   * @description Removes the regionalized subpath from the path if it exists
   */
  const removeRegionalizedSubPath = (path) => {
    const url = new URL(path);
    locales.forEach((locale) => {
      const regionalizedSubPath = `/${locale}`;
      if (url.pathname.startsWith(regionalizedSubPath)) {
        url.pathname = url.pathname.replace(regionalizedSubPath, "");
      }
    });
    return url;
  };

  const regionalizeLink = (path, locale = nextLocale) => {
    try {
      if (i18nDomains?.some((domain) => domain.nextLocale.includes(locale))) {
        // If the path is already regionalized, remove the regionalized subpath
        const regionalizedUrl = new URL(removeRegionalizedSubPath(path));

        // Set the hostname to the targeted locale based on what's defined in the publicConfig's i18nDomains property
        // Example: If the targeted locale is 'fr-ca', set the hostname to 'fr.shop.lululemon.com'
        regionalizedUrl.hostname = i18nDomains.find(
          (o) => o.nextLocale === locale
        ).hostname;

        // If the path is not regionalized, add the regionalized subpath if the targeted locale requires a regionalized subpath
        // Example: If the targeted locale is 'en-ca', add the regionalized subpath '/en-ca' to the path
        regionalizedUrl.pathname = isRegionalizedSubPath(locale)
          ? `/${locale}${regionalizedUrl.pathname}`
          : regionalizedUrl.pathname;

        return regionalizedUrl.href;
      }
    } catch (error) {
      // If an error occurs, log the error and return the original path (errors related to SEO should not break the site).
      // Errors related to SEO should be logged as warnings and captured via external monitoring tools (e.g. cloudwatch, sentry, and splunk)
      const renderType =
        typeof window !== "undefined" ? "client-side" : "server-side";
      log.warning("regionalizeLink() encountered an error", error.message, {
        renderType,
        pathToRegionalize: path,
        locale,
        error,
        stack: error.stack,
      });
    }

    return path;
  };

  return { regionalizeLink };
};

/**
 * Returns location title tag with Canada appended if Canadian locale
 *
 * @example
 * athletic apparel + technical clothing | lululemon Canada
 *
 * @param {usrLocale} boolean A boolean for if the RA flag is enabled
 * @param {nextLocale} string A string of the locale from Next router, e.x. en-ca
 * @param {title} string A string title of the page
 * @returns string for title tag content
 */
export const getRegionalizedTitleTag = (nextLocale, title) => {
  if (nextLocale?.toLowerCase() === "en-ca") {
    return `${title} Canada`;
  } else return title;
};

/**
 * @param {string} nextLocale - The locale from the next router
 * @returns {string} - Returns an i18n locale (e.g. en-ca, en-us, fr-ca) in the underscored format (e.g. en_US, fr_CA, en_CA)
 */
export const i18nToUnderscoredLocale = (nextLocale) => {
  const splitArr = nextLocale.split("-");
  return `${splitArr[0]}_${splitArr[1].toUpperCase()}`;
};

/**
 *
 * @param {string} underscoredLocale - The underscored locale format (e.g. en_US, fr_CA, en_CA)
 * @returns {string} - Returns an underscored locale (e.g. en_US, fr_CA, en_CA) in the i18n format (e.g. en-ca, en-us, fr-ca)
 */
export const underscoredLocaletoi18n = (underscoredLocale) => {
  const splitArr = underscoredLocale.split("_");
  return `${splitArr[0]}-${splitArr[1].toLowerCase()}`;
};

/**
 * Checks whether the given nextLocale is a regionalized subpath.
 *
 * @param {string} nextLocale - The nextLocale to check.
 * @returns {boolean} - true if the nextLocale is a regionalized subpath, false otherwise.
 */
export const isRegionalizedSubPath = (nextLocale) =>
  !!nextLocale && nextLocale?.toLowerCase() !== "en-us";

export const useLocaleHosts = () => {
  const { locale } = useRouter();
  const i18nDomains = publicConfig?.i18nDomains;

  const apiHost = i18nDomains?.find(
    (i18n) => i18n.nextLocale === locale
  )?.hostname;

  return { apiHost, i18nDomains };
};

/**
 * @param {string} i18nLocale - The locale from Next router, e.x. en-ca
 * @returns {string} - Returns the locale in ISO 3166 format (e.g. en-CA)
 */
export const i18nLocaleToIso3166 = (i18nLocale) => {
  return i18nLocale
    .split("-")
    .map((part, index) => (index === 1 ? part.toUpperCase() : part))
    .join("-");
};

/**
 * Removes locales from a given pathname.
 *
 * @param {string} pathname - The original pathname string.
 * @param {string[]} locales - An array of locales to be removed from the pathname.
 * @returns {string} The modified pathname with locales removed.
 */
export const removeLocalesFromPathname = (pathname) => {
  let locales = ["en-ca", "fr-ca"];
  if (typeof window !== "undefined") {
    locales = window?.__NEXT_DATA__?.locales;
  }
  //eslint-disable-next-line
  const localesRegex = new RegExp(`\\b(${locales.join("|")})\\b\/{0,1}`, "gi");
  return pathname.replace(localesRegex, "");
};

export const getRegionalizeRelativeUrl = (locale, defaultLocale, path) => {
  // For fr.shop.lll.com defaultLocale is fr-ca
  // For shop.lll.com defaultLocale is en-us
  return locale !== defaultLocale && path && !path.includes(locale)
    ? `/${locale}${path}`
    : path;
};
